import axios from "axios";
import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import {
    Card,
    Input,
    Button,
    Form,
    DatePicker,
    message,
    Descriptions,
    Divider,
    Typography,
} from "antd";
import { SearchOutlined, SaveOutlined, EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Title, Link } = Typography;

export default function AddCompoff() {
    const [empCode, setEmpCode] = useState("");
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const { authData, backUrl } = useContext(AuthContext);
    const navigate = useNavigate();

    // ✅ Fetch employee by code
    const fetchEmployee = async () => {
        if (!empCode.trim()) {
            message.warning("Please enter an employee code.");
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/employees?emp_code=${empCode}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );
            const result = res.data.results[0];
            if (!result) {
                message.error("No employee found with that code.");
                setEmployee(null);
            } else {
                setEmployee(result);
                message.success("Employee found!");
            }
        } catch (err) {
            message.error("Failed to fetch employee. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // ✅ Submit compoff
    const handleSubmit = async (values) => {
        if (!employee) {
            message.warning("Please search and select an employee first.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                employee: employee?.id,
                work_date: dayjs(values.work_date).format("YYYY-MM-DD"),
                reason: values.reason,
                created_by: authData?.employee?.id
            };

            await axios.post(
                `${backUrl}/api/hr/compoffs/`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            message.success("Comp-off added successfully!");
            form.resetFields();
            setEmployee(null);
            setEmpCode("");
        } catch (err) {
            let errMsg = "Failed to add comp-off.";

            if (err.response) {
                errMsg =
                    err.response.data?.message ||
                    err.response.data?.detail ||
                    JSON.stringify(err.response.data);
            } else if (err.request) {
                errMsg = "No response from server. Please try again.";
            } else {
                errMsg = err.message;
            }

            message.error(errMsg);
        } finally {
            setSubmitting(false);
        }
    };

    // ✅ Navigate to the new comp-off list page instead of opening a modal
    const handleViewCompoffs = () => {
        navigate("/hr/compoffs/my");
    };

    return (
        <div style={{ padding: "24px" }}>
            <Card
                style={{
                    maxWidth: 700,
                    margin: "0 auto",
                    borderRadius: 12,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                    }}
                >
                    <Title level={4} style={{ textAlign: "center", color: "#0e497b", margin: 0 }}>
                        Add Comp-Off
                    </Title>
                    {/* <Link
                        onClick={handleViewCompoffs}
                        style={{
                            position: "absolute",
                            right: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                        }}
                    >
                        <EyeOutlined /> View Compoffs
                    </Link> */}
                </div>

                <Divider />

                <Form.Item label="Search Employee" labelCol={{ span: 24 }}>
                    <Input.Search
                        placeholder="Enter Employee Code"
                        value={empCode}
                        onChange={(e) => setEmpCode(e.target.value)}
                        onSearch={fetchEmployee}
                        enterButton={
                            <Button
                                loading={loading}
                                icon={<SearchOutlined />}
                                style={{ backgroundColor: "#0e497b", color: "white" }}
                            >
                                Search
                            </Button>
                        }
                        onPressEnter={fetchEmployee}
                    />
                </Form.Item>

                {/* ✅ Employee Details */}
                {employee && (
                    <>
                        <Descriptions
                            bordered
                            size="small"
                            column={2}
                            style={{ marginBottom: 20 }}
                        >
                            <Descriptions.Item label="Employee Code">
                                {employee.emp_code || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Name">
                                {employee?.user?.first_name + " " + employee?.user?.last_name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Department">
                                {employee.department_name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Designation">
                                {employee.designation_name || "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider />

                        {/* ✅ Compoff Form */}
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmit}
                        >
                            <Form.Item
                                label="Work Date"
                                name="work_date"
                                rules={[{ required: true, message: "Please select the work date." }]}
                            >
                                <DatePicker
                                    style={{ width: "100%" }}
                                    format="DD/MM/YYYY"
                                    disabledDate={(current) =>
                                        current && current > dayjs().endOf("day")
                                    }
                                />
                            </Form.Item>

                            <Form.Item
                                label="Reason"
                                name="reason"
                                rules={[{ required: true, message: "Please enter a reason." }]}
                            >
                                <Input.TextArea
                                    rows={3}
                                    placeholder="Enter reason for comp-off..."
                                />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={submitting}
                                    icon={<SaveOutlined />}
                                    block
                                    style={{
                                        backgroundColor: "#0e497b",
                                        borderColor: "#0e497b",
                                        height: 40,
                                        borderRadius: 6,
                                    }}
                                >
                                    Submit Comp-Off
                                </Button>
                            </Form.Item>
                        </Form>
                    </>
                )}
            </Card>
        </div>
    );
}